import React, { useState } from "react";
import { Building2, Users, FileText, ExternalLink, ChevronRight } from "lucide-react";
import { PageHero, PageBody } from "./_shared";
import { useRoe } from "./RoeLayout";

const COLORS = [
  "from-blue-700 to-indigo-700",
  "from-emerald-600 to-teal-700",
  "from-rose-600 to-pink-700",
  "from-amber-600 to-orange-700",
  "from-purple-600 to-indigo-700",
  "from-cyan-600 to-blue-700",
];

export default function Committees() {
  const data = useRoe();
  const list = data.committees || [];
  const [activeId, setActiveId] = useState(list[0]?.id || null);

  const activeIdx = Math.max(
    0,
    list.findIndex((c) => c.id === activeId)
  );
  const active = list[activeIdx] || null;
  const grad = COLORS[activeIdx % COLORS.length];

  return (
    <div data-testid="roe-committees">
      <PageHero eyebrow="Governance" title="Committees & Cells" accent="blue" />
      <PageBody>
        {list.length === 0 ? (
          <div className="text-center bg-white rounded-xl shadow p-12 text-slate-500">
            Committee details will appear here once published.
          </div>
        ) : (
          <div className="grid md:grid-cols-[280px_1fr] gap-6">
            {/* Left: list of committees */}
            <aside
              className="bg-white rounded-xl shadow-md md:sticky md:top-24 self-start overflow-hidden"
              data-testid="roe-committees-list"
            >
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#A57C00] font-semibold">
                  Browse
                </div>
                <div className="font-serif text-lg text-slate-900">
                  All Committees ({list.length})
                </div>
              </div>
              <ul className="max-h-[70vh] overflow-y-auto">
                {list.map((c, i) => {
                  const isActive = c.id === active?.id;
                  const itemGrad = COLORS[i % COLORS.length];
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setActiveId(c.id)}
                        data-testid={`roe-committee-tab-${c.id}`}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 border-l-4 transition-colors ${
                          isActive
                            ? "bg-amber-50 border-amber-500"
                            : "border-transparent hover:bg-slate-50"
                        }`}
                      >
                        <span
                          className={`w-8 h-8 rounded-md bg-gradient-to-br ${itemGrad} text-white flex items-center justify-center shrink-0`}
                        >
                          <Building2 size={14} strokeWidth={1.5} />
                        </span>
                        <span
                          className={`flex-1 text-sm leading-snug ${
                            isActive ? "text-slate-900 font-semibold" : "text-slate-700"
                          }`}
                        >
                          {c.name}
                        </span>
                        <ChevronRight
                          size={14}
                          strokeWidth={1.5}
                          className={isActive ? "text-[#A57C00]" : "text-slate-300"}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Right: detail panel */}
            {active && (
              <article
                key={active.id}
                data-testid={`roe-committee-${active.id}`}
                className="bg-white rounded-xl overflow-hidden shadow-md"
              >
                <div
                  className={`bg-gradient-to-r ${grad} p-5 text-white flex items-center gap-3`}
                >
                  <div className="w-10 h-10 rounded-md bg-white/20 backdrop-blur flex items-center justify-center">
                    <Building2 size={18} strokeWidth={1.5} />
                  </div>
                  <h2 className="font-serif text-xl leading-tight">{active.name}</h2>
                </div>
                <div className="p-6">
                  {active.objective_html && (
                    <div className="mb-5">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#A57C00] font-semibold mb-1">
                        Objective
                      </div>
                      <div
                        className="prose prose-sm prose-slate max-w-none text-slate-700"
                        dangerouslySetInnerHTML={{ __html: active.objective_html }}
                      />
                    </div>
                  )}
                  {active.has_functions && active.functions_html && (
                    <div className="mb-5">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#A57C00] font-semibold mb-1">
                        Functions
                      </div>
                      <div
                        className="prose prose-sm prose-slate max-w-none text-slate-700"
                        dangerouslySetInnerHTML={{ __html: active.functions_html }}
                      />
                    </div>
                  )}
                  {(active.members || []).length > 0 && (
                    <div className="mt-4">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#A57C00] font-semibold mb-2 inline-flex items-center gap-1">
                        <Users size={11} strokeWidth={1.5} /> Members ({active.members.length})
                      </div>
                      <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md">
                        {active.members.map((m, idx) => (
                          <li
                            key={idx}
                            className="px-3 py-2 text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3"
                          >
                            <span className="font-medium text-slate-900">{m.name}</span>
                            {m.department && (
                              <span className="text-slate-500 text-xs">{m.department}</span>
                            )}
                            {m.designation && (
                              <span className="sm:ml-auto text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm bg-blue-50 text-[#001F3F] font-semibold">
                                {m.designation}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {active.has_pdf && active.pdf_url && (
                    <a
                      href={active.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-2 text-sm bg-amber-100 hover:bg-amber-200 text-amber-900 px-3 py-2 rounded-md font-medium"
                    >
                      <FileText size={14} strokeWidth={1.5} /> Open attached PDF{" "}
                      <ExternalLink size={11} strokeWidth={1.5} />
                    </a>
                  )}
                  {active.remarks && (
                    <div className="mt-5 text-xs text-slate-500 italic">{active.remarks}</div>
                  )}
                </div>
              </article>
            )}
          </div>
        )}
      </PageBody>
    </div>
  );
}
